import { C, DEMO_DURATION, along, background, banner, glow, pathLine, smooth, text, toPx } from "@/components/demos/canvasKit";
import { drawFleet } from "@/components/demos/drawFleet";
import { drawNet } from "@/components/demos/drawNet";

const EQUIP = [
  [0.3, 0.26, 0.32, 0.34, "MAIN ENGINE"],
  [0.72, 0.1, 0.16, 0.13, "GEN 1"],
  [0.72, 0.29, 0.16, 0.13, "GEN 2"],
  [0.72, 0.48, 0.16, 0.13, "GEN 3"],
  [0.06, 0.12, 0.14, 0.18, "PURIFIERS"],
  [0.42, 0.74, 0.16, 0.14, "BOILER"],
];
const PUMPS = [[0.12, 0.76, "P1"], [0.2, 0.76, "P2"], [0.28, 0.76, "P3"]];
const BOT_PATH = [[0.9, 0.9], [0.66, 0.9], [0.66, 0.68], [0.36, 0.68], [0.2, 0.67]];

const drawEquipment = (ctx, w, h, t) => {
  EQUIP.forEach(([x, y, ew, eh, label], i) => {
    ctx.fillStyle = "#111923";
    ctx.strokeStyle = C.landEdge;
    ctx.fillRect(x * w, y * h, ew * w, eh * h);
    ctx.strokeRect(x * w, y * h, ew * w, eh * h);
    text(ctx, label, (x + ew / 2) * w, (y + eh / 2) * h, { color: C.fg3, size: 10.5, align: "center" });
    glow(ctx, (x + ew) * w - 8, y * h + 8, 8, C.cyan, 0.5 + 0.4 * Math.sin(t * 2 + i));
  });
};

const drawPumps = (ctx, w, h, t, anomaly, isolated, repaired) => {
  PUMPS.forEach(([x, y, label]) => {
    let col = C.landEdge;
    if (label === "P2" && anomaly) col = C.amber;
    if (label === "P2" && isolated) col = "#4a5563";
    if (label === "P2" && repaired) col = C.green;
    if (label === "P3" && isolated) col = C.cyan;
    ctx.strokeStyle = col;
    ctx.fillStyle = "#111923";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(x * w, y * h, Math.min(w, h) * 0.032, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.lineWidth = 1;
    text(ctx, label, x * w, y * h, { color: col === C.landEdge ? C.fg3 : col, size: 10.5, align: "center" });
    if (label === "P2" && anomaly) glow(ctx, x * w, y * h, 30, "rgba(255,159,10,0.5)", 0.5 + 0.5 * Math.sin(t * 8));
    if (label === "P2" && repaired) text(ctx, "READY", x * w, y * h + 24, { color: C.green, size: 10, align: "center" });
  });
};

const drawBot = (ctx, p, ang, moving, t) => {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(ang);
  const gait = moving ? Math.sin(t * 14) * 3 : 0;
  ctx.strokeStyle = C.cyan;
  ctx.lineWidth = 1.6;
  [[-6, -1], [6, 1]].forEach(([lx, s]) => {
    ctx.beginPath();
    ctx.moveTo(lx, -5);
    ctx.lineTo(lx + gait * s, -11);
    ctx.moveTo(lx, 5);
    ctx.lineTo(lx - gait * s, 11);
    ctx.stroke();
  });
  ctx.fillStyle = C.cyan;
  ctx.beginPath();
  ctx.roundRect(-10, -5, 20, 10, 3);
  ctx.fill();
  ctx.restore();
  glow(ctx, p.x, p.y, 24, "rgba(0,242,254,0.3)");
};

export function drawRobot(ctx, w, h, t) {
  background(ctx, w, h, 36);
  const ph = (t % DEMO_DURATION) / DEMO_DURATION;
  const narrow = w < 560;
  const anomaly = ph >= 0.1 && ph < 0.2;
  const isolated = ph >= 0.2;
  const repairing = ph >= 0.5 && ph < 0.78;
  const repaired = ph >= 0.78;
  drawEquipment(ctx, w, h, t);
  drawPumps(ctx, w, h, t, anomaly, isolated, repaired);
  ctx.strokeStyle = C.fg3;
  ctx.strokeRect(0.86 * w, 0.86 * h, 0.09 * w, 0.08 * h);
  text(ctx, "DOCK", 0.905 * w, 0.84 * h, { color: C.fg3, size: 10.5, align: "center" });

  const px = toPx(BOT_PATH, w, h);
  let u = 0;
  if (ph >= 0.27 && ph < 0.5) u = smooth(0.27, 0.5, ph);
  else if (ph >= 0.5 && ph < 0.8) u = 1;
  else if (ph >= 0.8) u = 1 - smooth(0.8, 0.95, ph);
  const moving = (ph >= 0.27 && ph < 0.5) || (ph >= 0.8 && ph < 0.95);
  if (ph >= 0.27 && ph < 0.5) pathLine(ctx, px, u, 1, { dash: [4, 6], alpha: 0.7 });
  const p = along(px, u);
  drawBot(ctx, p, ph >= 0.8 ? p.ang + Math.PI : p.ang, moving, t);
  if (repairing) {
    const k = smooth(0.5, 0.78, ph);
    const pumpX = PUMPS[1][0] * w;
    const pumpY = PUMPS[1][1] * h;
    // The backup is already running. The robot now works on the isolated pump.
    ctx.strokeStyle = C.cyan;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(pumpX + Math.sin(t * 8) * 4, pumpY);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(pumpX, pumpY, Math.min(w, h) * 0.05, -Math.PI / 2, -Math.PI / 2 + k * Math.PI * 2);
    ctx.stroke();
    ctx.lineWidth = 1;
  }
  const label = repaired ? "P2 REPAIRED · READY AS BACKUP"
    : repairing ? "ROBOT REPAIRING P2"
    : ph >= 0.27 ? (narrow ? "ROBOT EN ROUTE" : "BACKUP RUNNING · ROBOT DISPATCHED")
    : isolated ? "BACKUP ON · ROC INFORMED"
    : anomaly ? "P2 FAULT DETECTED"
    : "P2 RUNNING · ALL SYSTEMS HEALTHY";
  banner(ctx, w, 12, label, anomaly ? C.amber : repaired ? C.green : C.cyan);
  if (isolated) text(ctx, "BACKUP P3 RUNNING · ROC INFORMED", w / 2, h - 15, { color: C.cyan, size: 10, align: "center" });
}

export const DRAWS = { fleet: drawFleet, net: drawNet, robot: drawRobot };
